import { useEffect, useState } from 'react';

import { logger } from './logger';

export function usePersistentState<T>(
  key: string,
  initialValue: T,
): [T, (value: T) => void] {
  const scopedKey = `persistentState-${key}`;

  const getValueFromLocalStorage = (): T => {
    try {
      const value = localStorage.getItem(scopedKey);
      if (value === null || value === undefined) {
        return initialValue;
      }
      const parsed = JSON.parse(value);
      return parsed as T;
    } catch (error) {
      logger.error(
        {
          userScopedKey: scopedKey,
          error: error instanceof Error ? error.message : 'Unknown error',
        },
        'Failed to parse persisted state value from localStorage',
      );
      // Clear the corrupted value
      localStorage.removeItem(scopedKey);
      return initialValue;
    }
  };

  const [state, setInternalState] = useState<T>(getValueFromLocalStorage);

  const setState = (value: T) => {
    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(scopedKey, serialized);
      setInternalState(value);
    } catch (error) {
      logger.error(
        {
          userScopedKey: scopedKey,
          value,
          error: error instanceof Error ? error.message : 'Unknown error',
        },
        'Failed to save state to localStorage',
      );
    }
  };

  return [state, setState];
}

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
